import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useProject } from '../../hooks/ProjectContext';

const STAGES = [
  { key: 'site-viewer',       label: '1. Site Viewer',       desc: 'Site boundary and evidence',      accept: '.kml,.geojson,.json' },
  { key: 'time-explorer',     label: '2. Time Explorer',     desc: 'Saved layer observations',        accept: '.tif,.tiff,.geojson,.json' },
  { key: 'stratification',    label: '3. Stratification',    desc: 'Strata runs and polygon outputs',  accept: '.kml,.geojson,.json' },
  { key: 'sampling-points',   label: '4. Sampling Points',   desc: 'Generated sample locations',       accept: '.kml,.geojson,.json' },
  { key: 'sampling-campaign', label: '5. Sampling Campaigns', desc: 'Planned soil sampling campaigns', accept: null },
  { key: 'field-records',     label: '6. Field Records',      desc: 'Execution records from field campaigns', accept: null },
];

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function LibraryPage() {
  const { selectedProject, selectedSite } = useProject();
  const navigate = useNavigate();
  const [artifacts, setArtifacts] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [fieldRecords, setFieldRecords] = useState([]);
  const [roiGeometry, setRoiGeometry] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [newRecordDialog, setNewRecordDialog] = useState(false);
  const [newRecordForm, setNewRecordForm] = useState({ campaignArtifactId: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [creatingRecord, setCreatingRecord] = useState(false);

  const loadArtifacts = useCallback(() => {
    if (!selectedProject?.code) { setArtifacts([]); return; }
    fetch(`/v1/stratification/artifacts?project=${encodeURIComponent(selectedProject.code)}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => setArtifacts(Array.isArray(rows) ? rows : []))
      .catch(() => setArtifacts([]));
  }, [selectedProject]);

  const loadFieldRecords = useCallback(() => {
    if (!selectedProject?.code) { setFieldRecords([]); return; }
    const params = new URLSearchParams({ project: selectedProject.code });
    if (selectedSite?.code) params.set('site', selectedSite.code);
    fetch(`/v1/field-records?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => setFieldRecords(Array.isArray(rows) ? rows : []))
      .catch(() => setFieldRecords([]));
  }, [selectedProject, selectedSite]);

  useEffect(() => { loadArtifacts(); }, [loadArtifacts]);
  useEffect(() => { loadFieldRecords(); }, [loadFieldRecords]);

  // Fetch geometry
  useEffect(() => {
    if (!selectedProject?.code) { setRoiGeometry(null); return; }
    const params = new URLSearchParams({ project: selectedProject.code });
    if (selectedSite?.code) params.set('site', selectedSite.code);
    fetch(`/v1/stratification/geometry?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(fc => setRoiGeometry(fc || null))
      .catch(() => setRoiGeometry(null));
  }, [selectedProject, selectedSite]);

  // Fetch evidence items
  useEffect(() => {
    if (!selectedProject?.code) { setEvidence([]); return; }
    const params = new URLSearchParams({ project: selectedProject.code });
    if (selectedSite?.code) params.set('site', selectedSite.code);
    fetch(`/v1/evidence?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => setEvidence(Array.isArray(rows) ? rows : []))
      .catch(() => setEvidence([]));
  }, [selectedProject, selectedSite]);

  async function handleDelete(id) {
    // Artifacts can be the canonical sampling design — never one stray click away.
    if (!window.confirm('Delete this artifact? This cannot be undone.')) return;
    const tkn = localStorage.getItem('tkn');
    await fetch(`/v1/stratification/artifacts/${id}`, {
      method: 'DELETE',
      headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
    });
    loadArtifacts();
  }

  async function handleDeleteFieldRecord(id) {
    if (!window.confirm('Delete this field record?')) return;
    const tkn = localStorage.getItem('tkn');
    await fetch(`/v1/field-records/${id}`, {
      method: 'DELETE',
      headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
    });
    loadFieldRecords();
  }

  async function createFieldRecord() {
    if (!selectedProject?.code || !newRecordForm.date) return;
    setCreatingRecord(true);
    // Resolve campaign name from selected artifact
    const campaign = campaigns.find(c => String(c.id) === String(newRecordForm.campaignArtifactId));
    const body = {
      projectId: selectedProject.code,
      siteId: selectedSite?.code || '',
      date: newRecordForm.date,
      notes: newRecordForm.notes || null,
      campaignArtifactId: newRecordForm.campaignArtifactId ? Number(newRecordForm.campaignArtifactId) : null,
      campaignName: campaign?.title || '',
    };
    const tkn = localStorage.getItem('tkn');
    const r = await fetch('/v1/field-records', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
      body: JSON.stringify(body),
    });
    const json = await r.json();
    setCreatingRecord(false);
    if (json.ok) {
      setNewRecordDialog(false);
      setNewRecordForm({ campaignArtifactId: '', date: new Date().toISOString().slice(0, 10), notes: '' });
      navigate(`/project/field-record/${json.id}`);
    }
  }

  // Parse KML text → GeoJSON FeatureCollection with stratum from folders
  function parseKmlToGeoJson(kmlText) {
    const doc = new DOMParser().parseFromString(kmlText, 'application/xml');
    if (doc.getElementsByTagName('parsererror')[0]) return null;

    const features = [];
    // Walk folders — each folder name becomes the stratum
    const folders = doc.getElementsByTagName('Folder');
    let stratumIdx = 0;

    function extractPlacemarks(parent, stratum) {
      const placemarks = parent.children ? Array.from(parent.children).filter(n => n.tagName === 'Placemark') : [];
      for (const pm of placemarks) {
        const nameEl = pm.getElementsByTagName('name')[0];
        const name = nameEl?.textContent || '';
        // Extract polygon coordinates
        const polygons = pm.getElementsByTagName('Polygon');
        for (const poly of Array.from(polygons)) {
          const coords = poly.getElementsByTagName('coordinates')[0]?.textContent?.trim();
          if (!coords) continue;
          const ring = coords.split(/\s+/).map(c => {
            const [lng, lat] = c.split(',').map(Number);
            return [lng, lat];
          }).filter(c => Number.isFinite(c[0]) && Number.isFinite(c[1]));
          if (ring.length < 3) continue;
          features.push({
            type: 'Feature',
            properties: { name, stratum },
            geometry: { type: 'Polygon', coordinates: [ring] },
          });
        }
        // Extract points
        const points = pm.getElementsByTagName('Point');
        for (const pt of Array.from(points)) {
          const coords = pt.getElementsByTagName('coordinates')[0]?.textContent?.trim();
          if (!coords) continue;
          const [lng, lat] = coords.split(',').map(Number);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
          features.push({
            type: 'Feature',
            properties: { name, stratum, label: name },
            geometry: { type: 'Point', coordinates: [lng, lat] },
          });
        }
      }
    }

    if (folders.length > 0) {
      for (const folder of Array.from(folders)) {
        const folderName = folder.getElementsByTagName('name')[0]?.textContent?.trim() || '';
        extractPlacemarks(folder, folderName || `Stratum ${stratumIdx}`);
        stratumIdx++;
      }
    } else {
      // No folders — extract all placemarks with stratum 0
      extractPlacemarks(doc, 0);
    }

    return features.length > 0 ? { type: 'FeatureCollection', features } : null;
  }

  async function handleUpload(tool, file) {
    if (!selectedProject?.code) return;
    setUploadStatus('Uploading...');
    try {
      const isTiff = file.name.match(/\.(tif|tiff|geotiff)$/i);

      if (isTiff) {
        // Binary upload — send raw bytes to backend for COG conversion + GCS storage
        setUploadStatus('Uploading GeoTIFF (' + (file.size / 1024 / 1024).toFixed(1) + ' MB)...');
        const params = new URLSearchParams({
          project: selectedProject.code,
          site: selectedSite?.code || '',
          title: file.name,
          tool,
        });
        const res = await fetch(`/v1/stratification/upload-raster?${params}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: file,
        });
        const json = await res.json();
        if (json.ok) {
          setUploadStatus('Uploaded: ' + file.name + ' → COG in GCS');
          loadArtifacts();
        } else {
          setUploadStatus('Upload failed: ' + (json.message || ''));
        }
        return;
      }

      // Text-based files (JSON, GeoJSON, KML)
      const text = await file.text();
      let payload = null;
      let params = { originalName: file.name };
      let subtype = 'upload';

      try {
        const parsed = JSON.parse(text);
        if (parsed.type === 'FeatureCollection' || parsed.type === 'Feature') {
          payload = parsed;
          params.format = 'geojson';
          if (parsed.features?.some(f => f.properties?.stratum !== undefined)) subtype = 'strata';
        } else {
          payload = parsed;
          params.format = 'json';
        }
      } catch {
        if (file.name.endsWith('.kml')) {
          const geojson = parseKmlToGeoJson(text);
          if (geojson) {
            payload = geojson;
            params.format = 'kml-parsed';
            if (geojson.features.some(f => f.geometry?.type === 'Polygon')) subtype = 'strata';
            else if (geojson.features.some(f => f.geometry?.type === 'Point')) subtype = 'points';
          } else {
            params.format = 'kml';
            params.rawContent = text.length > 500000 ? text.substring(0, 500000) : text;
          }
        } else {
          params.format = 'raw';
          params.rawContent = text.length > 500000 ? text.substring(0, 500000) : text;
        }
      }

      const res = await fetch('/v1/stratification/artifacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: selectedProject.code,
          siteId: selectedSite?.code || null,
          tool, subtype, title: file.name, params, payload,
        }),
      });
      const json = await res.json();
      if (json.ok) {
        setUploadStatus('Uploaded: ' + file.name);
        loadArtifacts();
      } else {
        setUploadStatus('Upload failed.');
      }
    } catch (e) {
      setUploadStatus('Upload error: ' + (e?.message || ''));
    }
  }

  const grouped = {};
  for (const a of artifacts) {
    const key = a.tool || 'other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  }
  const campaigns = artifacts.filter(a => a.tool === 'sampling-campaign');

  return (
    <>
      <Helmet><title>Library | TERRRA</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Typography variant="h4" gutterBottom>Library</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          All saved artifacts for {selectedProject ? <strong>{selectedProject.code}{selectedSite ? ` / ${selectedSite.code}` : ''}</strong> : 'the selected project'}.
          Upload additional files or download results for each stage.
          {' '}Edited a KML outside the platform? <Button size="small" onClick={() => navigate('/project/settings?tab=geometry')}>Review external edit</Button>
        </Typography>

        {!selectedProject && (
          <Alert severity="info">Select a project in the header to see the library.</Alert>
        )}

        {uploadStatus && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setUploadStatus('')}>{uploadStatus}</Alert>
        )}

        {/* Site boundary card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Site Boundary</Typography>
            {roiGeometry ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {roiGeometry.features?.length || 0} polygon{roiGeometry.features?.length !== 1 ? 's' : ''} loaded from database.
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {roiGeometry.features?.map((f, i) => (
                    <Chip key={i} label={f.properties?.name || `Polygon ${i + 1}`} size="small" sx={{ mb: 0.5 }} />
                  ))}
                </Stack>
              </>
            ) : (
              <Typography color="text.secondary">
                {selectedProject ? 'No polygons found.' : 'Select a project.'}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Evidence section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6">Field Evidence</Typography>
                <Typography variant="body2" color="text.secondary">
                  Photos, observations, and field data captured on site.
                </Typography>
              </Box>
              {evidence.length > 0 && (
                <Button
                  size="small" variant="outlined"
                  component="a"
                  href={`/evidence.kml?projectId=${selectedProject?.code || ''}`}
                  download
                >
                  Download all as KML
                </Button>
              )}
            </Stack>

            {evidence.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {selectedProject ? 'No evidence items found.' : 'Select a project.'}
              </Typography>
            ) : (() => {
              // Group by category
              const byCategory = {};
              for (const e of evidence) {
                const cat = e.category || 'uncategorized';
                if (!byCategory[cat]) byCategory[cat] = [];
                byCategory[cat].push(e);
              }
              return (
                <Stack spacing={1.5}>
                  <Typography variant="body2">
                    {evidence.length} item{evidence.length !== 1 ? 's' : ''} across {Object.keys(byCategory).length} categor{Object.keys(byCategory).length !== 1 ? 'ies' : 'y'}.
                  </Typography>
                  {Object.entries(byCategory).map(([cat, items]) => (
                    <Box key={cat}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, textTransform: 'capitalize' }}>
                        {cat} ({items.length})
                      </Typography>
                      <Stack spacing={0.5}>
                        {items.map(ev => (
                          <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: 'grey.200' }}>
                              <img
                                src={`/v1/evidence/${ev.id}/image`}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="bold" noWrap>
                                {ev.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {fmtDate(ev.captured_at)}
                                {ev.site_id && <> &middot; {ev.site_id}</>}
                                {ev.sampling_point && <> &middot; <strong>{ev.sampling_point}</strong></>}
                                {ev.notes && <> &middot; {ev.notes.substring(0, 60)}</>}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {ev.lat?.toFixed(4)}, {ev.lng?.toFixed(4)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              );
            })()}
          </CardContent>
        </Card>

        {/* Stage sections */}
        {STAGES.map(stage => {
          const items = grouped[stage.key] || [];

          // Stage 5: Sampling Campaigns — artifact-based, each has own actions
          if (stage.key === 'sampling-campaign') {
            return (
              <Card key={stage.key} sx={{ mb: 3 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="h6">{stage.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{stage.desc}</Typography>
                    </Box>
                    <Button variant="outlined" size="small" component="label">
                      Import CSV
                      <input hidden type="file" accept=".csv"
                        onChange={async e => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file || !selectedProject?.code) return;
                          try {
                            const text = await file.text();
                            const lines = text.split('\n').filter(l => l.trim());
                            const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
                            const idxOf = k => headers.findIndex(h => h.toLowerCase() === k.toLowerCase());
                            const rows = lines.slice(1).map(line => {
                              const cols = line.match(/("(?:[^"]|"")*"|[^,]*)/g)?.map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"')) || [];
                              return {
                                id: parseInt(cols[idxOf('ID')]) || 0,
                                name: cols[idxOf('Name')] || '',
                                stratum: cols[idxOf('Stratum')] || '',
                                lat: cols[idxOf('Latitude')] || '',
                                lng: cols[idxOf('Longitude')] || '',
                                appliedTier: cols[idxOf('Protocol')] || null,
                                tier: cols[idxOf('Protocol')] || null,
                                plotType: (cols[idxOf('Type')] || '').toLowerCase() === 'undisturbed' ? 'undisturbed' : 'composite',
                                cores: parseInt(cols[idxOf('Sample points')]) || 3,
                                depths: (cols[idxOf('Depths')] || '').split('/').map(d => d.trim()).filter(Boolean),
                                soc: cols[idxOf('SOC')] === '✓', bd: cols[idxOf('BD')] === '✓',
                                tom: cols[idxOf('TOM')] === '✓', texture: cols[idxOf('Texture')] === '✓',
                                inorganic: cols[idxOf('Inorganic')] === '✓', salinity: cols[idxOf('Salinity')] === '✓',
                                pom: cols[idxOf('POM')] === '✓', maom: cols[idxOf('MAOM')] === '✓',
                                availableDepths: [],
                              };
                            }).filter(r => r.name);
                            const name = file.name.replace(/\.csv$/i, '').trim();
                            const strataCount = new Set(rows.map(r => r.stratum).filter(Boolean)).size;
                            await fetch('/v1/stratification/artifacts', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
                              body: JSON.stringify({ projectId: selectedProject.code, tool: 'sampling-campaign', subtype: 'campaign', title: name, params: { strataCount, sampleCount: rows.length }, payload: { samplingData: rows, campaignName: name } }),
                            });
                            setUploadStatus(`Imported "${name}" — ${rows.length} sample points.`);
                            loadArtifacts();
                          } catch (err) { setUploadStatus('Import failed: ' + err.message); }
                        }} />
                    </Button>
                  </Stack>
                  {items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No campaigns yet. Use Sampling page → Save to Library, or import a CSV here.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {items.map(a => {
                        const params = (() => { try { return typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {}); } catch { return {}; } })();
                        const strataCount = params.strataCount ?? 0;
                        const sampleCount = params.sampleCount ?? 0;
                        const exportCsv = async () => {
                          // Need full payload for export — fetch the individual artifact
                          const r = await fetch(`/v1/stratification/artifacts/${a.id}`);
                          const art = await r.json();
                          const payload = (() => { try { return typeof art.payload_json === 'string' ? JSON.parse(art.payload_json) : (art.payload_json || {}); } catch { return {}; } })();
                          const pts = payload.samplingData || [];
                          const headers = ['ID', 'Name', 'Stratum', 'Latitude', 'Longitude', 'Protocol', 'Type', 'Sample points', 'Depths', 'SOC', 'BD', 'TOM', 'Texture', 'Inorganic', 'Salinity', 'POM', 'MAOM'];
                          const rows = pts.map(r => [r.id, r.name, r.stratum, r.lat, r.lng, r.appliedTier || r.tier || '', r.plotType === 'undisturbed' ? 'Undisturbed' : 'Composite', r.cores || '', (r.depths || []).join(' / '), r.soc ? '✓' : '', r.bd ? '✓' : '', r.tom ? '✓' : '', r.texture ? '✓' : '', r.inorganic ? '✓' : '', r.salinity ? '✓' : '', r.pom ? '✓' : '', r.maom ? '✓' : '']);
                          const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const el = document.createElement('a');
                          el.href = url; el.download = `${(a.title || 'campaign').replace(/[^a-z0-9]/gi, '_')}.csv`; el.click();
                          URL.revokeObjectURL(url);
                        };
                        return (
                          <Box key={a.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.title || 'Campaign'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {strataCount} strata · {sampleCount} samples · {fmtDate(a.created_at)}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5}>
                                <Button size="small" variant="outlined" onClick={exportCsv}>Export CSV</Button>
                                <Button size="small" variant="outlined"
                                  onClick={() => navigate(`/project/sampling?artifactId=${a.id}`)}>Open</Button>
                                <Button size="small" color="error" onClick={() => handleDelete(a.id)}>Delete</Button>
                              </Stack>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          }

          // Stage 6: Field Records
          if (stage.key === 'field-records') {
            return (
              <Card key={stage.key} sx={{ mb: 3 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="h6">{stage.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{stage.desc}</Typography>
                    </Box>
                    <Button variant="contained" size="small" onClick={() => setNewRecordDialog(true)} disabled={!selectedProject}>
                      New Field Record
                    </Button>
                  </Stack>
                  {fieldRecords.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No field records yet. Create one to track execution of a sampling campaign.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {fieldRecords.map(fr => {
                        const pct = fr.total_points > 0 ? Math.round((fr.done_points / fr.total_points) * 100) : 0;
                        return (
                          <Box key={fr.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {fr.date} · {fr.campaign_name || 'Field Record'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {fr.site_id ? `${fr.site_id} · ` : ''}{fr.done_points}/{fr.total_points} points
                                  {fr.total_points > 0 ? ` (${pct}%)` : ''}
                                  {fr.status === 'finalized' ? '' : ' · draft'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Chip
                                  label={fr.status === 'finalized' ? 'Finalized' : 'Draft'}
                                  color={fr.status === 'finalized' ? 'success' : 'default'}
                                  size="small"
                                />
                                <Button size="small" variant="outlined" onClick={() => navigate(`/project/field-record/${fr.id}`)}>
                                  Open
                                </Button>
                                {fr.status === 'draft' && (
                                  <Button size="small" color="error" onClick={() => handleDeleteFieldRecord(fr.id)}>
                                    Delete
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          }

          return (
            <Card key={stage.key} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="h6">{stage.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{stage.desc}</Typography>
                  </Box>
                  {stage.accept && (
                  <Button variant="outlined" size="small" component="label">
                    Upload file
                    <input
                      hidden type="file" accept={stage.accept}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) handleUpload(stage.key, f);
                      }}
                    />
                  </Button>
                  )}
                </Stack>

                {items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No items yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {items.map(a => {
                      const params = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                      return (
                        <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {a.title || a.subtype || 'Untitled'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {fmtDate(a.created_at)}
                              {a.subtype && <> &middot; {a.subtype}</>}
                              {params.layer && <> &middot; {params.layer}</>}
                              {params.nClusters && <> &middot; {params.nClusters} clusters</>}
                              {params.numPoints && <> &middot; {params.numPoints} points</>}
                              {params.format && <> &middot; {params.format}</>}
                            </Typography>
                          </Box>
                          {params.gcsPath && (
                            <Button size="small" component="a" href={`/v1/gcs/download?path=${encodeURIComponent(params.gcsPath)}`} download>
                              GeoTIFF
                            </Button>
                          )}
                          {params.assetId && a.tool === 'time-explorer' && (
                            <Chip size="small" label="GEE Asset" variant="outlined" />
                          )}
                          {(a.subtype === 'strata' || a.subtype === 'points') && (
                            <Button size="small" component="a" href={`/v1/stratification/artifacts/${a.id}/kml`} download>
                              KML
                            </Button>
                          )}
                          <Button size="small" color="error" onClick={() => handleDelete(a.id)}>
                            Delete
                          </Button>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Container>

      {/* New Field Record Dialog */}
      <Dialog open={newRecordDialog} onClose={() => setNewRecordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Field Record</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              select size="small" fullWidth label="Linked Campaign (optional)"
              value={newRecordForm.campaignArtifactId}
              onChange={e => {
                const id = e.target.value;
                const c = campaigns.find(c => String(c.id) === String(id));
                setNewRecordForm(f => ({ ...f, campaignArtifactId: id, campaignName: c?.title || '' }));
              }}
            >
              <MenuItem value="">— No campaign —</MenuItem>
              {campaigns.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" fullWidth label="Date" type="date"
              value={newRecordForm.date}
              onChange={e => setNewRecordForm(f => ({ ...f, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" fullWidth label="Notes (optional)" multiline rows={2}
              value={newRecordForm.notes}
              onChange={e => setNewRecordForm(f => ({ ...f, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewRecordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createFieldRecord} disabled={creatingRecord || !newRecordForm.date}>
            {creatingRecord ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
